// src/Components/admin/Modals/EditSubscriptionModal.jsx
// Corrects an existing subscription in place. The backend records each edit
// (who, when, old → new, reason) in the activity log.
import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { subscriptionAPI } from '../../services/api';
import { securityService } from '../serviceType';

const inputClass = "w-full bg-[#0A2A4A] border border-[#1A3A5A] rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500";
const labelClass = "block text-sm font-medium text-gray-300 mb-1";
const BILLING_CYCLES = ['Monthly', 'Quarterly', 'Annually'];
const CLOUD_PLAN = /^Cloud Storage - (\d+)TB$/;
const DATE = /^\d{2}\/\d{2}\/\d{4}$/;

const pick = (sub, ...keys) => {
  for (const k of keys) {
    if (sub?.[k] !== undefined && sub?.[k] !== null) return sub[k];
  }
  return '';
};

const isCloud = (sub) =>
  sub?.serviceType === 'CLOUD' ||
  (!sub?.serviceType && String(pick(sub, 'selected_plan', 'selectedPlan')).toLowerCase().includes('storage'));

// Form values from the subscription row (strings, so inputs stay controlled)
const initialValues = (sub) => {
  const plan = String(pick(sub, 'selected_plan', 'selectedPlan'));
  const tb = CLOUD_PLAN.exec(plan);
  return {
    Name: String(pick(sub, 'customer_name', 'customerName')),
    Email: String(pick(sub, 'email')),
    Phone: String(pick(sub, 'phone_number', 'phoneNumber')),
    Plan: plan,
    StorageTb: tb ? tb[1] : '',
    OS: String(pick(sub, 'selected_os', 'selectedOS')),
    Quantity: String(pick(sub, 'quantity') || 1),
    BillingCycle: String(pick(sub, 'billing_cycle', 'billingCycle')),
    Start: String(pick(sub, 'start_date', 'startDate')),
    Expiration: String(pick(sub, 'expiration_date', 'expirationDate')),
    DedicatedIPs: String(pick(sub, 'dedicated_ips', 'dedicatedIPs') || 0),
    Nvme: String(pick(sub, 'extraStorageNvmeGb') || 0),
    Hdd: String(pick(sub, 'extraStorageHddGb') || 0),
    CpuCores: String(pick(sub, 'cpuCores') || 0),
    RamGb: String(pick(sub, 'ramGb') || 0),
    Notes: String(pick(sub, 'notes')),
  };
};

const EditSubscriptionModal = ({ subscription, onClose, onSuccess }) => {
  const subscriptionId = pick(subscription, 'subscription_id', 'subscriptionId');
  const security = securityService(subscription);
  const cloud = !security && isCloud(subscription);
  const custom = Boolean(subscription?.custom || subscription?.isCustom);

  const initial = useMemo(() => initialValues(subscription), [subscription]);
  const cloudSized = cloud && initial.StorageTb !== '';

  const [values, setValues] = useState(initial);
  const [reason, setReason] = useState('');
  // Ticked by default: the customer is told about corrections unless the admin opts out
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [saving, setSaving] = useState(false);

  const set = (field) => (e) => setValues((prev) => ({ ...prev, [field]: e.target.value }));
  const changed = (field) => String(values[field]).trim() !== String(initial[field]).trim();

  // Only fields the admin actually changed are sent
  const buildPayload = () => {
    const payload = {};
    const text = (field) => { if (changed(field)) payload[field] = values[field].trim(); };
    const num = (field) => { if (changed(field)) payload[field] = Number(values[field]); };

    text('Name');
    text('Email');
    text('Phone');
    if (cloudSized) {
      if (changed('StorageTb')) payload.Plan = `Cloud Storage - ${values.StorageTb.trim()}TB`;
    } else {
      text('Plan');
    }
    text('Start');
    text('Expiration');
    if (changed('Notes')) payload.Notes = values.Notes.trim();
    if (!security) {
      num('Quantity');
      text('BillingCycle');
    }
    if (!security && !cloud) {
      text('OS');
      num('DedicatedIPs');
      if (changed('Nvme') || changed('Hdd')) {
        payload.ExtraStorage = { nvme: Number(values.Nvme), hdd: Number(values.Hdd) };
      }
      if (custom) {
        num('CpuCores');
        num('RamGb');
      }
    }
    return payload;
  };

  const validate = (payload) => {
    if (Object.keys(payload).length === 0) return 'No changes to save';
    if (!reason.trim()) return 'Please give a reason for the change';
    if (cloudSized && !/^[1-9]\d*$/.test(values.StorageTb.trim())) {
      return 'Storage size must be a whole number of at least 1 TB';
    }
    for (const f of ['Start', 'Expiration']) {
      if (payload[f] !== undefined && !DATE.test(payload[f])) return `${f} date must be DD/MM/YYYY`;
    }
    if (payload.Name !== undefined && !payload.Name) return 'Customer name cannot be empty';
    if (payload.Plan !== undefined && !payload.Plan) return 'Plan cannot be empty';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = buildPayload();
    const error = validate(payload);
    if (error) {
      toast.error(error);
      return;
    }
    try {
      setSaving(true);
      const res = await subscriptionAPI.adminEdit(subscriptionId, {
        ...payload,
        Reason: reason.trim(),
        NotifyCustomer: notifyCustomer,
      });
      toast.success(notifyCustomer
        ? 'Subscription updated, and the customer has been emailed'
        : 'Subscription updated');
      window.dispatchEvent(new Event('refreshSubscriptionsData'));
      onSuccess?.(res.data);
      onClose();
    } catch (err) {
      console.error('Error editing subscription:', err);
      toast.error(err.response?.data?.message || 'Failed to update subscription');
    } finally {
      setSaving(false);
    }
  };

  const field = (label, name, props = {}) => (
    <div className={props.wide ? 'col-span-2' : ''}>
      <label className={labelClass}>{label}</label>
      <input
        type={props.type || 'text'}
        value={values[name]}
        onChange={set(name)}
        className={`${inputClass} ${changed(name) ? 'border-yellow-500' : ''}`}
        min={props.min}
        max={props.max}
        maxLength={props.maxLength}
        placeholder={props.placeholder}
      />
    </div>
  );

  return (
    <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50 backdrop-blur-md" onClick={onClose}>
      <div className="bg-[#001F3F] border-2 border-[#022A52] rounded-3xl p-8 w-full max-w-2xl relative text-white max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-300 hover:text-red-500 text-2xl font-bold">×</button>

        <h2 className="text-2xl font-bold mb-1 text-center">Edit Subscription</h2>
        <p className="text-sm text-gray-400 text-center mb-6">{subscriptionId}. Changed fields are outlined in yellow.</p>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('Customer Name', 'Name', { wide: true, maxLength: 255 })}
            {field('Email', 'Email', { type: 'email', maxLength: 100 })}
            {field('Phone', 'Phone', { maxLength: 20 })}

            {cloudSized
              ? field('Storage size (TB)', 'StorageTb', { type: 'number', min: 1 })
              : field(security ? security.detailLabel : 'Plan', 'Plan', { wide: !cloud, maxLength: 255 })}

            {!security && !cloud && field('OS', 'OS', { maxLength: 255 })}
            {!security && field('Quantity', 'Quantity', { type: 'number', min: 1, max: 100 })}
            {!security && (
              <div>
                <label className={labelClass}>Billing Cycle</label>
                <select
                  value={values.BillingCycle}
                  onChange={set('BillingCycle')}
                  className={`${inputClass} ${changed('BillingCycle') ? 'border-yellow-500' : ''}`}
                >
                  {!BILLING_CYCLES.includes(values.BillingCycle) && <option value={values.BillingCycle}>{values.BillingCycle}</option>}
                  {BILLING_CYCLES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            )}

            {field(security ? 'Recorded On (DD/MM/YYYY)' : 'Start Date (DD/MM/YYYY)', 'Start')}
            {field(security ? 'Renewal Date (DD/MM/YYYY)' : 'Expiration (DD/MM/YYYY)', 'Expiration')}

            {!security && !cloud && custom && field('CPU (vCPU)', 'CpuCores', { type: 'number', min: 0, max: 64 })}
            {!security && !cloud && custom && field('RAM (GB)', 'RamGb', { type: 'number', min: 0, max: 512 })}
            {!security && !cloud && field('NVMe Storage (GB)', 'Nvme', { type: 'number', min: 0 })}
            {!security && !cloud && field('SSD Storage (GB)', 'Hdd', { type: 'number', min: 0 })}
            {!security && !cloud && field('Dedicated IPs', 'DedicatedIPs', { type: 'number', min: 0, max: 10 })}

            <div className="col-span-2">
              <label className={labelClass}>Notes</label>
              <textarea
                value={values.Notes}
                onChange={set('Notes')}
                rows={2}
                maxLength={2000}
                className={`${inputClass} ${changed('Notes') ? 'border-yellow-500' : ''}`}
              />
            </div>

            <div className="col-span-2">
              <label className={labelClass}>Reason for change *</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                maxLength={500}
                className={inputClass}
                placeholder="e.g. Customer purchased 3TB; it was entered as 2TB"
                required
              />
            </div>

            <label className="col-span-2 flex items-center gap-2 text-sm text-gray-300">
              <input
                type="checkbox"
                checked={notifyCustomer}
                onChange={(e) => setNotifyCustomer(e.target.checked)}
                className="h-4 w-4"
              />
              Send updated confirmation to customer
            </label>
          </div>

          <div className="flex justify-end space-x-4 mt-6">
            <button type="button" onClick={onClose} className="px-6 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg transition">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="px-6 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditSubscriptionModal;
